// Novedades reconocidas por la CUENTA (auditoría editorial 2026-09-28, E10).
//
// El fundador reportó que "Qué cambió" reaparecía una y otra vez. La causa
// que el código permitía demostrar: el visto vivía SOLO en localStorage
// (`mipae:que-cambio:visto:<userId>`), así que volvía a aparecer en cada
// navegador, en cada dominio que sirve la app (mipae.cl y mipae.app no
// comparten localStorage), en una ventana privada y en la app instalada
// cuando Android purga el almacenamiento del WebView (la misma causa del
// login perdido de #651, que la sesión ya resolvió mudándose a Preferences).
//
// Modelo (mismo patrón que lib/apariencia.js):
//   - La cuenta manda: `profiles.novedades_visto` guarda el orden más alto
//     reconocido (migración 20260928120000).
//   - El dispositivo es el espejo síncrono (lib/queCambio.js).
//   - Al resolver se toma el MÁXIMO de los dos y se empareja el que esté
//     atrás. Así un visto local anterior a esta versión sube a la cuenta
//     (migración conservadora: nadie vuelve a ver lo que ya cerró), y un
//     visto de otro aparato baja al espejo.
//   - Un fallo de red conserva el espejo local y se reintenta en la
//     próxima resolución (la escritura pendiente se detecta porque
//     local > cuenta). Nunca se declara sincronizado lo que falló.
//   - La resolución se cachea POR USUARIO durante la sesión: montajes
//     repetidos o una respuesta tardía no reabren el aviso. Cambiar de
//     cuenta no hereda nada (la clave es el userId).
import { supabase } from './supabase';
import { ordenVisto, guardarOrdenVisto, versionCatalogo } from './queCambio';

// Lee el orden reconocido en la cuenta. `null` si nunca se guardó; `undefined`
// si no se pudo leer (para distinguir "no hay" de "no sé").
export async function cargarNovedadesVisto(userId) {
  if (!userId) return undefined;
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('novedades_visto')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return undefined;
    const n = Number(data.novedades_visto);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return undefined;
  }
}

export async function guardarNovedadesVisto(userId, orden) {
  const n = Number(orden);
  if (!userId || !Number.isFinite(n) || n <= 0) return false;
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ novedades_visto: n })
      .eq('user_id', userId);
    return !error;
  } catch {
    return false;
  }
}

// Una resolución por usuario y por sesión. Exportado solo para tests.
const resueltas = new Map();
export function reiniciarCacheNovedades() {
  resueltas.clear();
}

// ¿Ya se resolvió el estado de esta cuenta en esta sesión? Síncrono, para que
// la card no pinte nada antes de saber (evita que aparezca y desaparezca).
export function novedadesResueltas(userId) {
  return resueltas.get(userId)?.listo === true;
}

// Empareja cuenta y dispositivo. Devuelve { visto, sincronizado }:
//   visto         orden reconocido resultante (0 = nada visto)
//   sincronizado  false si la cuenta quedó atrás porque la escritura falló o
//                 no se pudo leer; el espejo local igual queda al día.
export function resolverNovedadesVistas(userId) {
  if (!userId) return Promise.resolve({ visto: 0, sincronizado: false });
  const previa = resueltas.get(userId);
  if (previa) return previa.promesa;
  const entrada = { listo: false, promesa: null };
  entrada.promesa = (async () => {
    const local = ordenVisto(userId);
    const localNum = local === Infinity ? 0 : local;
    const cuenta = await cargarNovedadesVisto(userId);
    const cuentaNum = typeof cuenta === 'number' ? cuenta : 0;
    const visto = Math.max(localNum, cuentaNum);
    let sincronizado = cuenta !== undefined;
    if (visto > localNum) guardarOrdenVisto(userId, visto);
    if (cuenta !== undefined && visto > cuentaNum) {
      sincronizado = await guardarNovedadesVisto(userId, visto);
    }
    entrada.listo = true;
    return { visto, sincronizado };
  })();
  resueltas.set(userId, entrada);
  return entrada.promesa;
}

// Reconoce el conjunto vigente: espejo local al tiro (la card se cierra ya) y
// cuenta best-effort. Devuelve si la cuenta quedó al día. Si falla, el espejo
// conserva el cierre y `resolverNovedadesVistas` lo vuelve a subir en la
// próxima apertura (local > cuenta).
export async function marcarNovedadesVistas(userId) {
  const orden = versionCatalogo();
  guardarOrdenVisto(userId, orden);
  const ok = await guardarNovedadesVisto(userId, orden);
  // La próxima resolución de esta sesión parte del estado ya reconocido:
  // un montaje nuevo de la card no vuelve a preguntar ni a mostrar.
  resueltas.set(userId, { listo: true, promesa: Promise.resolve({ visto: orden, sincronizado: ok }) });
  return ok;
}
